import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Text } from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { FormContainer, StringInput } from "~/OnboardingApp/components";
import { usePartner } from "~/OnboardingApp/hooks";
import { useSubmitTrialActivation } from "~/OnboardingApp/hooks/useSubmitTrialActivation";
import { requiredString } from "~/utils/validations";
import { ActivateCta } from "./ActivateCta";

type Values = {
  code: string;
};

const validationSchema = Yup.object({
  code: requiredString,
});

// Best-effort pre-fill of the manual code field from the carried QR context.
// The carry credential is the opaque qr_code_key (never the plaintext
// activation code), so there is no human-readable code to inject today —
// redemption is resolved server-side from the qr_code_key in U7. This stays a
// documented seam: when a resolved plaintext code becomes available
// client-side it can be returned here; until then the field is empty so a
// scanner can type a code as the U7 fallback.
export const resolveActivateCode = (_: string): string => "";

export const ActivateCard: FC = () => {
  const { onSubmit, errorMessage } = useSubmitTrialActivation();
  const { t } = useTranslation();
  const { partnerMeta, qrCodeKey } = usePartner();
  const partnerName = partnerMeta?.name || "Partner";

  const initialValues: Values = {
    code: resolveActivateCode(qrCodeKey),
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const { setErrors } = formik;

  useEffect(() => {
    if (!errorMessage) return;

    setErrors({ code: errorMessage });
  }, [errorMessage, setErrors]);

  return (
    <Flex justify="center" w="full">
      <Flex
        bg="th.white"
        borderRadius="1rem"
        boxShadow="0px 14px 34px 0px rgba(0, 0, 0, 0.05)"
        maxW="26.25rem"
        pb="4rem"
        pt="3rem"
        px="2rem"
        w="full"
      >
        <FormContainer formik={formik}>
          <Flex direction="column" gap="2rem">
            <Text textAlign="center" variant="th-t1" w="full">
              {t("onboarding.activate.card.heading")}
            </Text>
            <Text variant="th-body2">
              {t("onboarding.activate.card.description", {
                partnerName: partnerName,
              })}
            </Text>
            <StringInput
              id="code"
              fontSize=".875rem"
              placeholder={t("onboarding.activate.card.placeholder", {
                partnerName: partnerName,
              })}
            />
          </Flex>
          <ActivateCta />
        </FormContainer>
      </Flex>
    </Flex>
  );
};
